import React from 'react';

import { useGetPortfolio } from '@ping/api';
import { parsePair } from '@ping/helpers';
import { useIsUserLoggedIn } from '@ping/hooks';
import { selectedMarketInstrumentSelector, useMarketStore } from '@ping/stores/market.store';
import { isNil } from '@ping/utils';

interface Token {
  id: string;
  label?: string;
}

export function useAvailablePortfolioTokens(assetId) {
  const isUserLoggedIn = useIsUserLoggedIn();
  const { data: portfolio } = useGetPortfolio({
    query: {
      enabled: isUserLoggedIn,
      // Ensure fresh data after order placements (query is invalidated elsewhere)
      staleTime: 0,
      refetchOnMount: 'always',
      refetchOnReconnect: true,
      select: data => {
        if (isNil(data?.portfolioItems)) {
          return new Map();
        }

        return data.portfolioItems.reduce((map, item) => map.set(item.assetId, item), new Map());
      },
    },
  });

  return React.useMemo<Token[]>(() => {
    if (!portfolio || portfolio.size === 0 || !assetId) {
      return [];
    }

    const { tokenIds, onHoldTokenIds = [] } = portfolio.get(assetId.toLowerCase()) ?? {};
    if (!tokenIds || tokenIds.length === 0) {
      return [];
    }

    const filtered = [];
    for (const tokenId of tokenIds) {
      if (!onHoldTokenIds.includes(tokenId)) {
        filtered.push({ id: tokenId, label: tokenId });
      }
    }
    return filtered.reverse();
  }, [portfolio, assetId]);
}

export function useAvailablePortfolioTokensOfSelectedMarket() {
  const instrument = useMarketStore(selectedMarketInstrumentSelector);
  const pair = parsePair.byInstrument(instrument);
  return useAvailablePortfolioTokens(pair?.base);
}
